import { Box, IconButton, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { useRef } from 'react';
import { tokens } from '../theme';
import api from '../services/api';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentUploadSection = ({
  title = 'Insira documentos do cliente',
  files = [],
  onAddFiles,
  onRemoveFile,
  accept = '.pdf,.jpg,.jpeg,.png',
  hint = 'Envie um ou mais arquivos (PDF, JPG, PNG)',
  maxFiles,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fileInputRef = useRef(null);

  const isSingleMode = maxFiles === 1;
  const hasFile = files.length > 0;

  const handleDropZoneClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) onAddFiles(isSingleMode ? [selected[0]] : selected);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length > 0) onAddFiles(isSingleMode ? [dropped[0]] : dropped);
  };

  const getDisplayInfo = (item) => {
    if (item instanceof File) {
      return { name: item.name, size: item.size, url: null };
    }

    return { name: item.original_name, size: item.size, url: item.url };
  };

  const openFile = async (path) => {
    const response = await api.get(`/v1/file/${path}`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(response.data);

    window.open(url);
  };

  return (
    <Box mb={4}>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: '#374151' }}>
        {title}
      </Typography>

      {/* Drop zone */}
      <Box
        onClick={handleDropZoneClick}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1.5px dashed #D1D5DB',
          borderRadius: '8px',
          padding: '12px 16px',
          cursor: 'pointer',
          mb: 1.5,
          '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
          transition: 'all 0.15s',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: '#F3F4F6',
            }}
          >
            <AttachFileIcon
              sx={{ color: colors.greenAccent[500], fontSize: 24, transform: 'rotate(45deg)' }}
            />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} fontSize={14} color="#374151">
              {isSingleMode && hasFile ? 'Substituir arquivo' : 'Anexar documento'}
            </Typography>
            <Typography variant="caption" color="#6B7280">
              {hint}
            </Typography>
          </Box>
        </Box>
        <AddIcon sx={{ color: colors.greenAccent[500], fontSize: 22 }} />
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple={!isSingleMode}
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* File list */}
      {files.map((item, index) => {
        const { name, size, url } = getDisplayInfo(item);
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '10px 14px',
              mb: 1,
              backgroundColor: '#F9FAFB',
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} sx={{ overflow: 'hidden' }}>
              <InsertDriveFileIcon sx={{ color: '#6B7280', fontSize: 20, flexShrink: 0 }} />
              {url ? (
                <Typography
                  component="a"
                  onClick={(e) => {
                    e.preventDefault();
                    openFile(url);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  fontSize={14}
                  fontWeight={500}
                  sx={{
                    color: '#2563EB',
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {name}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  fontSize={14}
                  color="#374151"
                  fontWeight={500}
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {name}
                </Typography>
              )}
              {size && (
                <Typography variant="caption" color="#9CA3AF" sx={{ flexShrink: 0 }}>
                  {formatFileSize(size)}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => onRemoveFile(index)}
              sx={{ color: '#9CA3AF', flexShrink: 0, '&:hover': { color: '#EF4444' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
};

export default DocumentUploadSection;
